import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { 
  Save, ArrowLeft, Building2, Landmark, Search, ShieldCheck, 
  Users, Mail, Phone, Plus, Trash2, Paperclip, ChevronLeft, 
  ChevronRight, Type, Minus, Briefcase, Info, MapPin
} from 'lucide-react';

// المكونات المشتركة
import UniversalLookup from './UniversalLookup';
import AttachmentsModal from './AttachmentsModal';
import { useFontSize } from './FontSizeContext';
import SmartPrintButton from './PrintButton'; 
import { usePermissions } from './usePermissions';

const VendorDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  
  // 1. الإعدادات العامة والخط
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;
  const [activeTab, setActiveTab] = useState('GENERAL');
  const [selectedContactIndex, setSelectedContactIndex] = useState(null);
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionOrg = session.org_id || '';
  const { canCreate, canUpdate, canDelete } = usePermissions('vendors');

  // 2. بيانات المورد (شاملة حقول العنوان الجديدة)
  const [vendorData, setVendorData] = useState({
    comp_num: '', 
    name: '', 
    type: 'VENDOR', 
    currency: 'EGP',
    trn: '', 
    bank: '', 
    gl_account: '', 
    parent: '', 
    org_id: sessionOrg,
    address: '', // حقل العنوان الجديد
    city: '',    // حقل المدينة الجديد
    state: '',   // حقل الولاية الجديد
    country: ''  // حقل الدولة الجديد
  });

  // 3. حالات جهات الاتصال والمرفقات والنافيجيشن
  const [contacts, setContacts] = useState([]);
  const [attachCount, setAttachCount] = useState(0);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', field: '', domainId: '' });
  const [fieldMaps, setFieldMaps] = useState({});
  

  // جلب الميتاداتا
  useEffect(() => {
    const fetchMetadata = async () => {
        try {
            const [orgRes, siteRes] = await Promise.all([
                axios.get('/api/admin/field-map/companies'),
                axios.get('/api/admin/field-map/sites')
            ]);
            setFieldMaps({ ...orgRes.data, ...siteRes.data });
        } catch (e) { console.error("Metadata Load Error"); }
    };
    fetchMetadata();
  }, []);

  // محرك التنقل
  const loadNavigation = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await axios.get(`/api/companies/${id}/navigation${location.search}`);
      setNav(res.data || { first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
    } catch (err) { console.error("Nav failed"); }
  }, [id, isNew, location.search]);

  // جلب بيانات المورد وجهات الاتصال
  const loadData = useCallback(async () => {
    if (!isNew) {
      try {
        const [vendRes, contRes] = await Promise.all([
          axios.get(`/api/companies/${id}`),
          axios.get(`/api/contacts/COMPANIES/${id}`)
        ]);
        setVendorData(vendRes.data);
        setContacts(contRes.data || []);
        if (contRes.data?.length > 0) setSelectedContactIndex(0); // اختيار أول سطر
        fetchCount(vendRes.data.comp_num);
        loadNavigation();
      } catch (err) { toast.error("Error fetching data"); }
    }
  }, [id, isNew, loadNavigation]);

  const fetchCount = async (num) => {
    try {
      const res = await axios.get(`/api/attachments/count/COMPANIES/${num}`);
      setAttachCount(res.data.count || 0);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadData(); }, [loadData]);

  const handleInputChange = (field, value) => {
    setVendorData(prev => ({ ...prev, [field]: value }));
  };

  // الحفظ الشامل
  const handleSave = async () => {
    if (!vendorData.comp_num || !vendorData.name) {
      return toast.error("Company ID and Name are required!");
    }
    
    const loadId = toast.loading("Saving Data...");
    try {
      const token = localStorage.getItem('token');
      const payload = { ...vendorData };
      
      // تنظيف البيانات
      Object.keys(payload).forEach(key => { if (payload[key] === "") payload[key] = null; });

      // 1. حفظ المورد
      await axios.post('/api/companies/save', payload, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // 2. حفظ جهات الاتصال
      const contactsPayload = contacts.map(c => ({
          ...c,
          ownertable: 'COMPANIES',
          ownerid: vendorData.comp_num
      }));

      await axios.post('/api/contacts/save-all', contactsPayload, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success("Saved Successfully", { id: loadId });
      if (isNew) navigate(`/vendors/${vendorData.comp_num}`);
    } catch (err) {
      // 🎯 تصحيح خطأ React #31 هنا
      let errorMessage = "Save Failed";
      
      if (err.response?.status === 422) {
        // لو الخطأ 422، الـ detail بيكون array، لازم نحوله لنص
        errorMessage = "Validation Error: Please check required fields";
        console.log("Validation Details:", err.response.data.detail);
      } else if (typeof err.response?.data?.detail === 'string') {
        errorMessage = err.response.data.detail;
      }

      toast.error(errorMessage, { id: loadId });
    }
  };

  // الحذف
  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "This will delete the company and its contacts!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    });
    if (result.isConfirmed) {
      try {
        await axios.delete(`/api/companies/${id}`);
        toast.success("Deleted successfully");
        navigate('/vendors');
      } catch (err) { toast.error("Delete failed"); }
    }
  };
  const handleSendEmail = async () => {
      const firstContactEmail = contacts.length > 0 ? contacts[0].email : null;
      if (!firstContactEmail) return toast.error("Vendor email is missing!");
      const result = await Swal.fire({
        title: 'Send SLA Penality?', text: `Do you want to email this request to ${vendorData.comp_num}?`,
        icon: 'question', showCancelButton: true, confirmButtonColor: '#1a3353'
      });
      if (result.isConfirmed) {
        try {
          toast.loading("Sending Email...");
          await axios.post(`/api/vendor/send-email/${vendorData.comp_id}`);
          toast.dismiss(); toast.success("Email sent successfully!");
        } catch (err) { toast.dismiss(); toast.error("Email failed"); }
      }
    };
  // إدارة جهات الاتصال
  const addContactRow = () => {
    const newRow = { 
      contact_name: '', position: '', email: '', phone: '', 
      mobile: '', personal_email: '', address: '', city: '', country: '' 
    };
    setContacts([...contacts, newRow]);
    setSelectedContactIndex(contacts.length);
  };

  const handleContactChange = (idx, field, val) => {
    const updated = [...contacts];
    updated[idx][field] = val;
    setContacts(updated);
  };

  const handleSmartSearch = (fieldName, title, defaultType) => {
      const linkedDomain = fieldMaps[fieldName];
      setLookup({ 
          isOpen: true, title, 
          type: linkedDomain ? 'DOMAIN' : defaultType, 
          domainId: linkedDomain || '', field: fieldName
      });
  };

  const goTo = (target) => target && navigate(`/vendors/${target}${location.search}`);

  return (
    <div className="flex flex-col h-screen bg-[#f3f4f6] font-sans overflow-hidden" style={{ fontSize: `${fontSize}px` }}>
      <UniversalLookup 
        {...lookup} 
        onClose={() => setLookup({...lookup, isOpen: false})} 
        onSelect={(row) => {
            const val = row.comp_num || row.org_id || row.value || row.id;
            if (activeTab === 'CONTACTS' && selectedContactIndex !== null) {
              handleContactChange(selectedContactIndex, lookup.field, val);
            } else {
              handleInputChange(lookup.field, val);
            }
            setLookup(prev => ({ ...prev, isOpen: false }));
        }} 
      />

      {/* --- TOP TOOLBAR --- */}
      <div className="bg-[#1a3353] text-white p-1.5 flex justify-between items-center shadow-lg z-30 px-4 shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/vendors')} className="p-1.5 hover:bg-white/10 rounded-full text-blue-200">
            <ArrowLeft size={18} />
          </button>
          {!isNew && (
            <div className="flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1">
              <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3}/></button>
              <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16}/></button>
              <div className="px-2 text-[10px] font-mono border-x border-white/10 mx-1"><span className="text-orange-400">{nav.current_pos}</span> / {nav.total}</div>
              <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16}/></button>
              <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} strokeWidth={3}/></button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!isNew && <button onClick={handleSendEmail} className="bg-blue-600 hover:bg-blue-700 px-4 py-1.5 rounded-sm flex items-center gap-2 font-black shadow-md"><Mail size={14}/> SEND EMAIL</button>}
          {((isNew && canCreate) || (!isNew && canUpdate)) && (
          <button onClick={handleSave} className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-sm flex items-center gap-2 text-[11px] font-black shadow-md transition-all">
            <Save size={14} /> SAVE
          </button>
          )}
          <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 hover:bg-white/10 rounded text-blue-200" title="Attachments">
            <Paperclip size={16} />
            {attachCount > 0 && <span className="absolute top-1 right-1 bg-red-600 text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black">{attachCount}</span>}
          </button>
          {((!isNew && canDelete)) && (
          <button onClick={handleDelete} className="p-2 hover:bg-red-600/20 rounded text-red-400" title="Delete"><Trash2 size={16}/></button>
          )}
          <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/20 ml-4">
            <button onClick={() => fontContext.changeFontSize(-1)} className="hover:text-orange-400"><Minus size={14} /></button>
            <div className="flex items-center gap-1 min-w-[40px] justify-center text-blue-200">
              <Type size={14} /><span className="font-black text-[10px]">{fontSize}</span>
            </div>
            <button onClick={() => fontContext.changeFontSize(1)} className="hover:text-orange-400"><Plus size={14} /></button>
          </div>
          {!isNew && (
          <SmartPrintButton 
            moduleName="VENDOR" 
            refId={`${vendorData.comp_num}`} 
           // تمرير البارامتر المطلوب
          />)}
        </div>
      </div>

      {/* --- HEADER FIELDS --- */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-12 shadow-sm shrink-0 z-20">
        <div className="flex flex-col border-r pr-8 border-gray-100">
          <label className="text-[8px] text-gray-400 font-black uppercase italic tracking-widest leading-none mb-1">Company</label>
          <input 
            className={`w-24 border-b border-transparent outline-none font-black text-blue-900 uppercase bg-transparent ${!isNew ? 'opacity-60' : 'focus:border-blue-500'}`}
            value={vendorData.comp_num}
            disabled={!isNew}
            onChange={(e) => handleInputChange('comp_num', e.target.value.toUpperCase())}
            placeholder="NEW"
          />
        </div>
        <div className="flex flex-col flex-1">
          <label className="text-[8px] text-gray-400 font-black uppercase italic tracking-widest leading-none mb-1">Company Description</label>
          <input 
            className="w-full border-b border-transparent outline-none text-blue-800 font-bold bg-transparent focus:border-blue-500 transition-all"
            value={vendorData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="Enter Company Name..."
          />
        </div>
      </div>

      {/* --- TABS --- */}
      <div className="flex bg-white border-b border-gray-100 px-4 shrink-0 font-black uppercase tracking-tighter">
        <button onClick={() => setActiveTab('GENERAL')} 
          className={`px-8 py-3 flex items-center gap-2 transition-all ${activeTab === 'GENERAL' ? 'border-b-2 border-blue-900 text-blue-900' : 'text-gray-400 hover:text-gray-600'}`}>
          <Briefcase size={13}/> Vendor Details
        </button>
        <button onClick={() => setActiveTab('CONTACTS')} 
          className={`px-8 py-3 flex items-center gap-2 transition-all ${activeTab === 'CONTACTS' ? 'border-b-2 border-blue-900 text-blue-900' : 'text-gray-400 hover:text-gray-600'}`}>
          <Users size={13}/> Contact Persons
        </button>
      </div>

      {/* --- CONTENT AREA --- */}
      <div className="flex-1 overflow-y-auto p-4 bg-[#f8f9fb] custom-scrollbar">
        {activeTab === 'GENERAL' ? (
          <div className="flex flex-col gap-6 max-w-7xl mx-auto animate-in fade-in duration-500 pb-10">
            <div className="grid grid-cols-12 gap-6">
                <div className="col-span-8">
                  <SectionCard title="Company Information">
                    <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                      <div className="flex flex-col border-b border-gray-200 pb-1">
                        <label className="text-[8px] font-black text-gray-400 uppercase italic">Company Type</label>
                        <select className="font-bold text-blue-900 outline-none bg-transparent" value={vendorData.type} onChange={(e) => handleInputChange('type', e.target.value)}>
                          <option value="VENDOR">Vendor (Supplier)</option>
                          <option value="MANUFACTURER">Manufacturer</option>
                          <option value="INTERNAL">Internal Company</option>
                        </select>
                      </div>
                      <MaximoLookupField label="Parent Company" value={vendorData.parent} onSearch={() => handleSmartSearch('parent', 'Parent Companies', 'vendors')} />
                      <MaximoInput label="Tax Registration (TRN)" value={vendorData.trn} onChange={(v) => handleInputChange('trn', v)} />
                      <MaximoLookupField label="Organization" value={vendorData.org_id} onSearch={() => handleSmartSearch('org_id', 'Organizations', 'organizations')} />
                    </div>
                  </SectionCard>
                </div>
                <div className="col-span-4">
                  <SectionCard title="Financials">
                    <div className="space-y-4">
                      <MaximoLookupField label="Currency" value={vendorData.currency} onSearch={() => handleSmartSearch('currency', 'Currencies', 'DOMAIN')} />
                      <MaximoInput label="Bank Name" value={vendorData.bank} onChange={(v) => handleInputChange('bank', v)} />
                      <MaximoInput label="GL Account" value={vendorData.gl_account} onChange={(v) => handleInputChange('gl_account', v)} />
                    </div>
                  </SectionCard>
                </div>
            </div>

            {/* قسم العنوان الخاص بالشركة */}
            <SectionCard title="Primary Address & Location Details">
                <div className="grid grid-cols-4 gap-8">
                    <MaximoInput label="Street Address" value={vendorData.address} onChange={(v) => handleInputChange('address', v)} />
                    <MaximoInput label="City" value={vendorData.city} onChange={(v) => handleInputChange('city', v)} />
                    <MaximoInput label="State / Province" value={vendorData.state} onChange={(v) => handleInputChange('state', v)} />
                    <MaximoLookupField label="Country" value={vendorData.country} onSearch={() => handleSmartSearch('country', 'Select Country', 'DOMAIN')} />
                </div>
            </SectionCard>
          </div>
        ) : (
          <div className="flex flex-col gap-4 max-w-7xl mx-auto animate-in slide-in-from-right duration-500 h-full pb-20">
            {/* جدول جهات الاتصال */}
            <div className="bg-white border rounded shadow-lg overflow-hidden shrink-0">
              <div className="p-2 bg-slate-50 border-b flex justify-between items-center">
                <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest italic flex items-center gap-2">
                  <Users size={14}/> Master Contact List
                </span>
                <button onClick={addContactRow} className="bg-blue-900 text-white px-3 py-1 rounded-sm text-[9px] font-black flex items-center gap-1 hover:bg-blue-800 transition-all">
                  <Plus size={12}/> ADD CONTACT
                </button>
              </div>
              <div className="max-h-[250px] overflow-y-auto">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-gray-50 border-b text-[9px] font-black uppercase text-gray-400 sticky top-0 z-10">
                      <tr>
                        <th className="p-3 border-r w-10 text-center">#</th>
                        <th className="p-3 border-r">Contact Name</th>
                        <th className="p-3 border-r">Position</th>
                        <th className="p-3 border-r">Official Email</th>
                        <th className="p-3 w-10 text-center text-red-400"><Trash2 size={12}/></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {contacts.length === 0 ? (
                        <tr><td colSpan="5" className="p-10 text-center text-gray-300 italic">No contacts defined.</td></tr>
                      ) : contacts.map((c, idx) => (
                        <tr key={idx} 
                          onClick={() => setSelectedContactIndex(idx)} 
                          className={`cursor-pointer transition-colors ${selectedContactIndex === idx ? 'bg-blue-100/50' : 'hover:bg-blue-50'}`}>
                          <td className="p-3 border-r text-center font-bold text-gray-400">{idx + 1}</td>
                          <td className="p-1 border-r"><input className="w-full bg-transparent outline-none p-2 font-bold text-blue-900 uppercase" value={c.contact_name || ''} onChange={e => handleContactChange(idx, 'contact_name', e.target.value)} /></td>
                          <td className="p-1 border-r"><input className="w-full bg-transparent outline-none p-2 italic text-gray-600" value={c.position || ''} onChange={e => handleContactChange(idx, 'position', e.target.value)} /></td>
                          <td className="p-1 border-r"><input className="w-full bg-transparent outline-none p-2 text-blue-600" value={c.email || ''} onChange={e => handleContactChange(idx, 'email', e.target.value)} /></td>
                          <td className="p-1 text-center">
                            <button onClick={(e) => { e.stopPropagation(); setContacts(contacts.filter((_, i) => i !== idx)); }} className="p-2 text-gray-300 hover:text-red-600"><Trash2 size={14} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
              </div>
            </div>

            {/* قسم تفاصيل العنوان والاتصال للشخص المختار (Master-Detail) */}
            {selectedContactIndex !== null && contacts[selectedContactIndex] && (
              <div className="animate-in slide-in-from-bottom duration-300">
                <SectionCard title={`Extended Details: ${contacts[selectedContactIndex].contact_name || 'New Member'}`}>
                  <div className="grid grid-cols-4 gap-x-8 gap-y-6">
                    <MaximoInput label="Work Phone" value={contacts[selectedContactIndex].phone} onChange={(v) => handleContactChange(selectedContactIndex, 'phone', v)} />
                    <MaximoInput label="Mobile / WhatsApp" value={contacts[selectedContactIndex].mobile} onChange={(v) => handleContactChange(selectedContactIndex, 'mobile', v)} />
                    <MaximoInput label="Personal Email" value={contacts[selectedContactIndex].personal_email} onChange={(v) => handleContactChange(selectedContactIndex, 'personal_email', v)} />
                    <MaximoLookupField label="Country" value={contacts[selectedContactIndex].country} onSearch={() => handleSmartSearch('country', 'Select Country', 'DOMAIN')} />
                    <div className="col-span-2">
                        <MaximoInput label="Full Address" value={contacts[selectedContactIndex].address} onChange={(v) => handleContactChange(selectedContactIndex, 'address', v)} />
                    </div>
                    <MaximoInput label="City" value={contacts[selectedContactIndex].city} onChange={(v) => handleContactChange(selectedContactIndex, 'city', v)} />
                    <div className="bg-orange-50 p-3 rounded border border-orange-100 flex items-center gap-3">
                        <Info size={16} className="text-orange-500" />
                        <p className="text-[9px] text-orange-700 font-bold uppercase italic tracking-tighter">Information linked to selected contact row above.</p>
                    </div>
                  </div>
                </SectionCard>
              </div>
            )}
          </div>
        )}
      </div>

      {isDocModalOpen && (
        <AttachmentsModal isOpen={isDocModalOpen} ownertable="COMPANIES" ownerid={vendorData.comp_num} onClose={() => {setIsDocModalOpen(false); fetchCount(vendorData.comp_num);}} />
      )}
    </div>
  );
};

// --- Reusable Components (Visual Style Consistency) ---
const SectionCard = ({ title, children }) => (
  <div className="bg-white border rounded shadow-sm overflow-hidden transition-all hover:shadow-md shrink-0">
    <div className="p-2 bg-slate-50 border-b font-black text-slate-500 uppercase text-[9px] tracking-widest flex items-center gap-2 italic">
      <div className="w-1.5 h-3 bg-blue-800 rounded-full"></div> {title}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const MaximoInput = ({ label, value, onChange }) => (
  <div className="flex flex-col border-b border-gray-200 py-1 focus-within:border-blue-500 transition-all">
    <label className="text-[8px] font-black text-gray-400 uppercase italic leading-none mb-1">{label}</label>
    <input 
        className="font-bold text-blue-900 outline-none bg-transparent py-0.5" 
        value={value || ''} 
        onChange={(e) => onChange(e.target.value)} 
    />
  </div>
);

const MaximoLookupField = ({ label, value, onSearch }) => (
  <div className="flex flex-col border-b border-gray-200 py-1 focus-within:border-blue-500 transition-all">
    <label className="text-[8px] font-black text-gray-400 uppercase italic leading-none mb-1">{label}</label>
    <div className="flex items-center justify-between pb-0.5 h-6">
      <span className="font-black text-blue-900 uppercase truncate">{value || '---'}</span>
      <Search size={12} className="text-blue-500 cursor-pointer hover:scale-110 transition-transform" onClick={onSearch} />
    </div>
  </div>
);

export default VendorDetails;